import { Head, Link, router } from "@inertiajs/react"

import { AppShell } from "@/components/AppShell"
import {
  BidStatusBadge,
  PRICING_FORMAT_LABELS,
  WASTE_HANDLING_LABELS,
} from "@/components/bids/BidStatusBadge"
import { NewBidDialog } from "@/components/bids/NewBidDialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "@/components/ui/table"
import { EM_DASH, pct } from "@/lib/money"
import { relative } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import type { BidsIndexProps } from "@/types/bids"

const DESCRIPTION =
  "One bid per community: the builder's pricing format, product lineup, and per-level quoted prices."

const FILTERS = [
  ["all", "All"],
  ["draft", "Draft"],
  ["active", "Active"],
  ["rebid", "Rebid needed"],
] as const

export default function BidsIndex({
  bids,
  filter,
  counts,
  available_communities,
  builders,
}: BidsIndexProps) {
  return (
    <>
      <Head title="Bids">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Bids" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell wide>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-6">
          <div>
            <h1>Bids</h1>
            <p className="mt-1">
              One bid per community. Each carries the builder&apos;s pricing format and its own
              product lineup.
            </p>
          </div>
          <NewBidDialog communities={available_communities} builders={builders} />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => router.get("/bids", key === "all" ? {} : { filter: key }, { preserveState: true })}
              className={cn(
                "rounded-full border px-3 py-1 text-sm",
                filter === key
                  ? "border-accent bg-accent-faded text-accent-display"
                  : "border-hairline text-ink-muted hover:text-ink-body"
              )}
              data-testid={`bid-filter-${key}`}
            >
              {label} <span className="tabular-nums">· {counts[key]}</span>
            </button>
          ))}
        </div>

        {bids.length === 0 ? (
          <div className="callout mt-6 max-w-lg">
            <p>
              {counts.all === 0
                ? "No bids yet. Start one from a production builder's community page, or with + New bid."
                : "Nothing matches this filter."}
            </p>
          </div>
        ) : (
          <div className="mt-4" data-testid="bids-table">
            <Table>
              <TableHead>
                <tr>
                  <TableHeadCell>Community</TableHeadCell>
                  <TableHeadCell>Builder</TableHeadCell>
                  <TableHeadCell>Pricing format</TableHeadCell>
                  <TableHeadCell className="text-right">Plans</TableHeadCell>
                  <TableHeadCell className="text-right">Blended GM</TableHeadCell>
                  <TableHeadCell>Status</TableHeadCell>
                  <TableHeadCell>Updated</TableHeadCell>
                </tr>
              </TableHead>
              <TableBody>
                {bids.map((row) => (
                  <TableRow
                    key={row.community.id}
                    href={row.url}
                    data-testid={`bid-row-${row.community.id}`}
                  >
                    <TableCell>
                      <Link href={row.url} className="font-medium text-ink-display">
                        {row.community.name}
                      </Link>
                      {row.community.locality && (
                        <span className="text-ink-muted"> · {row.community.locality}</span>
                      )}
                    </TableCell>
                    <TableCell>{row.customer_name}</TableCell>
                    <TableCell className="text-ink-muted">
                      {PRICING_FORMAT_LABELS[row.pricing_format]} ·{" "}
                      {WASTE_HANDLING_LABELS[row.waste_handling]}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.plan_count > 0 ? row.plan_count : EM_DASH}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{pct(row.blended_gm)}</TableCell>
                    <TableCell>
                      <BidStatusBadge status={row.status} rebidNeeded={row.rebid_needed} />
                    </TableCell>
                    <TableCell className="text-ink-muted">{relative(row.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </AppShell>
    </>
  )
}
